import React, { ChangeEvent, useMemo } from 'react';
import Button from '../Button';
import FontAwesomeIcon from '../Icon';
import Table from '../Table';
import Empty from '../Empty';
import Loader from '../Loader';
import Skeleton from 'src/components/Skeleton/Skeleton';
import Search from 'src/components/Search/Search';
import ContactForm from 'src/components/ContactForm/ContactForm';
import { ITableColumn } from 'src/components/Table/types';
import {
  sendActivationLink,
  sendResetPasswordLink,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';
import { PropertyManagerRow } from 'src/pages/SharedPageCustomHooks/UseContactsSavePropertiesPage/types';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import './PropertyDevelopersTable.scss';

const SKELETON_ROW_COUNT: number = 13;

interface PropertyDevelopersTableProps {
  propertyDevelopers: PropertyManagerRow[];
  columns: ITableColumn[];
  loading: boolean;
  globalLoaderVisible: boolean;
  showOverlay: boolean;
  editingExistingContactInTable: boolean;
  allowedContactFormRoles: string[];
  canAddContact: boolean;
  searchIsActive: string;
  onSearch: (e: ChangeEvent<HTMLInputElement>) => void;
  onOpenAddContact: () => void;
  onCloseContactForm: () => void;
  onSubmitContactForm: (
    formData: IContactFormData,
    formType: string
  ) => Promise<void>;
}

const PropertyDevelopersTable = ({
  propertyDevelopers,
  columns,
  loading,
  globalLoaderVisible,
  showOverlay,
  editingExistingContactInTable,
  allowedContactFormRoles,
  canAddContact,
  searchIsActive,
  onSearch,
  onOpenAddContact,
  onCloseContactForm,
  onSubmitContactForm,
}: PropertyDevelopersTableProps) => {
  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  const skeletonColumns: ITableColumn[] = useMemo(
    () =>
      columns.map((column: ITableColumn) => ({
        ...column,
        cell: () => <Skeleton variant="text" width="70%" />,
      })),
    [columns]
  );

  const skeletonData: any[] = useMemo(
    () =>
      Array.from({ length: SKELETON_ROW_COUNT }, (_, index: number) => ({
        id: `developer-skeleton-${index}`,
      })),
    []
  );

  return (
    <>
      {globalLoaderVisible ? <Loader center={true} /> : null}
      <div className="property-developers-table-header">
        <div className="property-developers-table-search">
          <Search
            size="small"
            placeholder={'Search People...'}
            onChange={onSearch}
            theme={undefined}
          />
        </div>
        {canAddContact ? (
          <Button
            color="teal"
            label={'Add Contact'}
            onClick={onOpenAddContact}
            icon={
              <FontAwesomeIcon
                glyph="user-plus"
                weight="regular"
                size="small"
              />
            }
            size="small"
          />
        ) : null}
      </div>
      {loading ? (
        <Table
          columns={skeletonColumns}
          data={skeletonData}
          paginate={{ itemsPerPage, position: 'both' }}
          paginationSkeleton
          onItemsPerPageChange={onItemsPerPageChange}
        />
      ) : propertyDevelopers && propertyDevelopers.length ? (
        <Table
          columns={columns}
          // legacy/workaround: Table's data union doesn't include PropertyManagerRow yet
          data={propertyDevelopers as any}
          paginate={{ itemsPerPage, position: 'both' }}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      ) : (
        <Empty
          direction="ttb"
          headline={'No people yet'}
          strapline={
            "Keep all your contacts here so it's easier for you to manage."
          }
          searchIsActive={searchIsActive}
          cta={
            canAddContact ? (
              <Button
                color="teal"
                label={'Add Person'}
                onClick={onOpenAddContact}
                icon={
                  <FontAwesomeIcon glyph="user" weight="light" size="small" />
                }
                size="small"
              />
            ) : null
          }
          image={<img alt="No people yet" src="/assets/empty/contacts.svg" />}
        />
      )}
      {showOverlay && (
        <ContactForm
          onClose={onCloseContactForm}
          onSubmit={onSubmitContactForm}
          sendResetPasswordLinkToUser={sendResetPasswordLink}
          sendActivationLinkToUser={sendActivationLink}
          allowedRoles={allowedContactFormRoles}
          pageUsedFrom="Properties"
          editingExistingContactInTable={editingExistingContactInTable}
        />
      )}
    </>
  );
};

export default PropertyDevelopersTable;
